import React, { useEffect, useState } from 'react';
import { BookOpen, ChevronDown, ChevronUp, HelpCircle, ArrowRight } from 'lucide-react';

const STORAGE_KEY = 'instagram_osint_guide_open';

/** Optional detail. Collapsed by default so the checklist is the living path. */
export const InstagramOsintGuide: React.FC = () => {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored === '1') setOpen(true);
            else if (stored === '0') setOpen(false);
        } catch {
            /* ignore */
        }
    }, []);

    const toggle = () => {
        setOpen((v) => {
            const next = !v;
            try {
                localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
            } catch {
                /* ignore */
            }
            return next;
        });
    };

    return (
        <section
            className="border border-border rounded-lg bg-card overflow-hidden"
            data-tour="instagram-guide"
            aria-labelledby="ig-guide-heading"
        >
            <button
                type="button"
                onClick={toggle}
                className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-secondary/40 transition-colors"
                aria-expanded={open}
            >
                <BookOpen className="w-4 h-4 text-muted-foreground shrink-0" />
                <span id="ig-guide-heading" className="text-sm font-medium flex-1">
                    How this desk works (optional)
                </span>
                {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>

            {open && (
                <div className="px-4 pb-4 space-y-4 text-sm border-t border-border pt-3">
                    <p className="text-muted-foreground">
                        This page pulls wallets, UPI, and phones from the <strong className="text-foreground">suspect&apos;s own</strong>{' '}
                        bio or screenshots. Handle lookup searches <strong className="text-foreground">this case, P2P ads, and MuleHunter</strong> —
                        it does not log into Instagram and does not invent followers.
                    </p>

                    <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-medium">
                        {[
                            '1 Case + identity',
                            '2 Bio / screenshots',
                            '3 Find leads',
                            '4 Save',
                            '5 Workbench',
                        ].map((label, i) => (
                            <React.Fragment key={label}>
                                {i > 0 && <ArrowRight className="w-3 h-3 text-muted-foreground/60" />}
                                <span className="px-2 py-1 rounded-md bg-secondary text-secondary-foreground">
                                    {label}
                                </span>
                            </React.Fragment>
                        ))}
                    </div>

                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                            What to click (first time)
                        </div>
                        <ol className="list-decimal list-inside space-y-1.5 text-muted-foreground">
                            <li>
                                Pick the <strong className="text-foreground">case</strong>, then either paste the{' '}
                                <strong className="text-foreground">@handle</strong> or search with{' '}
                                <strong className="text-foreground">phone / email / UPI</strong>
                            </li>
                            <li>
                                Stay on <strong className="text-foreground">Paste bio &amp; links</strong> — copy the Instagram
                                bio text into the box (highest yield)
                            </li>
                            <li>
                                Optional: <strong className="text-foreground">Upload screenshots</strong> for payment QRs / posts
                            </li>
                            <li>
                                Press <strong className="text-foreground">Find leads</strong>
                            </li>
                            <li>
                                After results, press{' '}
                                <strong className="text-foreground">Save to case &amp; open Workbench</strong> — wallets land on
                                the graph. After two+ handles on the same case, use Compare IG accounts.
                            </li>
                        </ol>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <div className="rounded-md border border-border p-3 space-y-1">
                            <div className="text-xs font-semibold text-foreground">Good things to paste / upload</div>
                            <ul className="list-disc list-inside text-[12px] text-muted-foreground space-y-0.5">
                                <li>Bio with rates, UPI (<code className="text-[11px]">name@oksbi</code>), TRC20 wallets</li>
                                <li>Linktree / t.me / wa.me links</li>
                                <li>Screenshots of UPI QR or “DM for USDT”</li>
                            </ul>
                        </div>
                        <div className="rounded-md border border-border p-3 space-y-1">
                            <div className="text-xs font-semibold text-foreground">How to read the table</div>
                            <ul className="list-disc list-inside text-[12px] text-muted-foreground space-y-0.5">
                                <li>
                                    <span className="text-green-600 font-medium">Usable</span> — format looks right; still confirm
                                    on-chain
                                </li>
                                <li>
                                    <span className="text-amber-600 font-medium">Needs review</span> — weak / uncertain; double-check
                                </li>
                                <li>Rows are leads, never courtroom proof by themselves</li>
                            </ul>
                        </div>
                    </div>

                    <div className="flex items-start gap-2 text-[13px] text-muted-foreground bg-secondary/40 rounded-md px-3 py-2">
                        <HelpCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>
                            Compare IG accounts only after you save <strong className="text-foreground">two or more different @handles</strong> on
                            the same case. Shared wallet or UPI is the link — not Instagram friends.
                        </span>
                    </div>
                </div>
            )}
        </section>
    );
};

export default InstagramOsintGuide;
